import { Component, OnInit, Input, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import {
  TtdPartner,
  TtdAdvertiser,
  TtdPartnerCreateDto,
  TtdPartnerUpdateDto
} from '../../../../models/services/admin/ttd';

export type AdminTTDDialogMode =
  | 'createPartner' | 'editPartner' | 'deletePartner'
  | 'createAdvertiser' | 'editAdvertiser' | 'deleteAdvertiser';

export type AdminTTDDialogClose =
  | { action: 'cancel' }
  | { action: 'createPartner', data: TtdPartnerCreateDto }
  | { action: 'updatePartner', id: string, data: TtdPartnerUpdateDto, newId?: string }
  | { action: 'deletePartner', id: string }
  | { action: 'createAdvertiser', partnerId: string, data: TtdAdvertiser }
  | { action: 'updateAdvertiser', partnerId: string, id: string, data: Pick<TtdAdvertiser, 'name'>, newId?: string }
  | { action: 'deleteAdvertiser', partnerId: string, id: string };

@Component({
  selector: 'admin-ttd-dialog',
  templateUrl: './admin-ttd-dialog.component.html',
  styleUrls: ['./admin-ttd-dialog.component.sass'],
  encapsulation: ViewEncapsulation.None
})
export class AdminTTDDialogComponent implements OnInit {
  @Input() mode: AdminTTDDialogMode = 'createPartner';
  @Input() partner: TtdPartner | null = null;
  @Input() usedIds: Set<string> = new Set<string>();
  @Input() advertiser: TtdAdvertiser | null = null;
  @Input() usedAdvIds: Set<string> = new Set<string>();

  public accept: (res: AdminTTDDialogClose) => void = () => {};
  public dismiss: () => void = () => {};

  loading = false;
  title = '';

  form = this.fb.group({
    id: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]+$/)]],
    name: ['', [Validators.required, Validators.maxLength(120)]],
    firstPartyDataSecretKey: [''],
    advertisers: this.fb.array([])
  });

  advForm = this.fb.group({
    id: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]+$/)]],
    name: ['', [Validators.required, Validators.maxLength(120)]],
  });

  constructor(private fb: FormBuilder) {}

  get f() { return this.form.controls; }
  get idCtrl(): AbstractControl { return this.form.get('id')!; }
  get advertisersFA(): FormArray { return this.form.get('advertisers') as FormArray; }
  get isPartnerForm() { return this.mode === 'createPartner' || this.mode === 'editPartner'; }
  get isPartnerDelete() { return this.mode === 'deletePartner'; }
  get isAdvertiserForm() { return this.mode === 'createAdvertiser' || this.mode === 'editAdvertiser'; }
  get isAdvertiserDelete() { return this.mode === 'deleteAdvertiser'; }
  get advIdCtrl(): AbstractControl { return this.advForm.get('id')!; }

  private uniqueIdValidator = (taken: Set<string>, original?: string): ValidatorFn =>
    (ctrl: AbstractControl) => {
      const v = (ctrl.value ?? '').toString().trim();
      if (!v) return null;
      return taken.has(v) && v !== original ? { notUnique: true } : null;
    };

  private setTitle() {
    const map: Record<AdminTTDDialogMode, string> = {
      createPartner: 'Create partner',
      editPartner:   'Edit partner',
      deletePartner: 'Delete partner',
      createAdvertiser: 'Add advertiser',
      editAdvertiser:   'Edit advertiser',
      deleteAdvertiser: 'Delete advertiser'
    };
    this.title = map[this.mode] ?? 'Dialog';
  }

  ngOnInit(): void {
    this.setTitle();
    if (this.isPartnerForm) {
      if (this.partner) {
        this.form.patchValue({
          id: this.partner.id,
          name: this.partner.name,
          firstPartyDataSecretKey: this.partner.firstPartyDataSecretKey ?? ''
        });

        const existing = this.idCtrl.validator ? [this.idCtrl.validator] : [];
        const uniq = this.uniqueIdValidator(this.usedIds, this.partner.id);
        this.idCtrl.setValidators(Validators.compose([...existing, uniq]));
        this.idCtrl.updateValueAndValidity({ emitEvent: false });

        this.advertisersFA.clear();
        (this.partner.advertisers ?? []).forEach(a =>
          this.advertisersFA.push(this.buildAdvGroup(a.id, a.name))
        );
      } else {
        const existing = this.idCtrl.validator ? [this.idCtrl.validator] : [];
        const uniq = this.uniqueIdValidator(this.usedIds);
        this.idCtrl.setValidators(Validators.compose([...existing, uniq]));
        this.idCtrl.updateValueAndValidity({ emitEvent: false });
      }
    }

    if (this.isPartnerDelete && this.partner) {
      this.form.patchValue({
        id: this.partner.id,
        name: this.partner.name,
        firstPartyDataSecretKey: this.partner.firstPartyDataSecretKey ?? ''
      });
      this.form.disable();
    }

    if (this.isAdvertiserForm) {
      if (this.mode === 'editAdvertiser' && this.advertiser) {
        this.advForm.patchValue({ id: this.advertiser.id, name: this.advertiser.name });
        const existing = this.advIdCtrl.validator ? [this.advIdCtrl.validator] : [];
        const uniq = this.uniqueIdValidator(this.usedAdvIds, this.advertiser.id);
        this.advIdCtrl.setValidators(Validators.compose([...existing, uniq]));
        this.advIdCtrl.updateValueAndValidity({ emitEvent: false });
      } else {
        const existing = this.advIdCtrl.validator ? [this.advIdCtrl.validator] : [];
        const uniq = this.uniqueIdValidator(this.usedAdvIds);
        this.advIdCtrl.setValidators(Validators.compose([...existing, uniq]));
        this.advIdCtrl.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  buildAdvGroup(id = '', name = '') {
    return this.fb.group({
      id:   [id,   [Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]+$/)]],
      name: [name, [Validators.required, Validators.maxLength(120)]],
    });
  }
  addAdvertiser() { this.advertisersFA.push(this.buildAdvGroup()); }
  removeAdvertiser(i: number) { this.advertisersFA.removeAt(i); }
  trackByIndex = (i: number) => i;

  onAccept(): void {
    if (this.isPartnerDelete && this.partner) {
      this.accept({ action: 'deletePartner', id: this.partner.id });
      return;
    }
    if (this.isAdvertiserDelete && this.partner && this.advertiser) {
      this.accept({ action: 'deleteAdvertiser', partnerId: this.partner.id, id: this.advertiser.id });
      return;
    }

    if (this.isPartnerForm) {
      this.form.markAllAsTouched();
      this.idCtrl.updateValueAndValidity();
      if (this.form.invalid) return;

      const raw = this.form.getRawValue();
      const newId = raw.id!.trim();

      if (this.mode === 'createPartner') {
        const createDto: TtdPartnerCreateDto = {
          id: newId,
          name: raw.name!.trim(),
          firstPartyDataSecretKey: raw.firstPartyDataSecretKey?.trim() || undefined,
          advertisers: (raw.advertisers ?? []).map(a => ({ id: a.id.trim(), name: a.name.trim() }))
        };
        this.accept({ action: 'createPartner', data: createDto });
        return;
      }

      if (this.mode === 'editPartner' && this.partner) {
        const idChanged = newId !== this.partner.id;
        if (idChanged && this.usedIds.has(newId)) {
          this.idCtrl.setErrors({ notUnique: true });
          return;
        }
        const updateDto: TtdPartnerUpdateDto = {
          name: raw.name!.trim(),
          firstPartyDataSecretKey: raw.firstPartyDataSecretKey?.trim() || undefined,
          advertisers: (raw.advertisers ?? []).map(a => ({ id: a.id.trim(), name: a.name.trim() }))
        };
        this.accept({
          action: 'updatePartner',
          id: this.partner.id,
          data: updateDto,
          ...(idChanged ? { newId } : {})
        });
        return;
      }
    }

    if (this.isAdvertiserForm && this.partner) {
      this.advForm.markAllAsTouched();
      this.advIdCtrl.updateValueAndValidity();
      if (this.advForm.invalid) return;

      const advRaw = this.advForm.getRawValue();
      const newAdvId = advRaw.id!.trim();

      if (this.mode === 'createAdvertiser') {
        const adv: TtdAdvertiser = { id: newAdvId, name: advRaw.name!.trim() };
        this.accept({ action: 'createAdvertiser', partnerId: this.partner.id, data: adv });
        return;
      }

      if (this.mode === 'editAdvertiser' && this.advertiser) {
        const idChanged = newAdvId !== this.advertiser.id;
        if (idChanged && this.usedAdvIds.has(newAdvId)) {
          this.advIdCtrl.setErrors({ notUnique: true });
          return;
        }
        this.accept({
          action: 'updateAdvertiser',
          partnerId: this.partner.id,
          id: this.advertiser.id,
          data: { name: advRaw.name!.trim() },
          ...(idChanged ? { newId: newAdvId } : {})
        });
        return;
      }
    }
  }
}
